// Respostas Rápidas no padrão "Aeros Dark" — mockup "Tela Aeros Dark -
// Respostas Rápidas". Grid de cards (ou lista), filtros em pills com
// contagem, busca por atalho ou texto e ações copiar/editar/excluir.
import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { toast } from "react-toastify";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import InputBase from "@material-ui/core/InputBase";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import Skeleton from "@material-ui/lab/Skeleton";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import GridViewIcon from "@material-ui/icons/Apps";
import ViewListIcon from "@material-ui/icons/ViewList";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import GroupOutlinedIcon from "@material-ui/icons/GroupOutlined";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import ImageOutlinedIcon from "@material-ui/icons/ImageOutlined";
import MicNoneIcon from "@material-ui/icons/MicNone";
import VideocamOutlinedIcon from "@material-ui/icons/VideocamOutlined";
import PictureAsPdfOutlinedIcon from "@material-ui/icons/PictureAsPdfOutlined";
import InsertDriveFileOutlinedIcon from "@material-ui/icons/InsertDriveFileOutlined";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import { isArray } from "lodash";

import MainContainer from "../../components/MainContainer";
import QuickMessageDialog from "../../components/QuickMessageDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { AEROS, stateText } from "../../styles/aerosTokens";
import {
  FILTERS,
  applyFilters,
  mediaKind,
  isShared,
  initials,
} from "./quickMessageRules";

const reducer = (state, action) => {
  if (action.type === "LOAD_QUICKMESSAGES") {
    const quickmessages = action.payload;
    const newQuickmessages = [];

    if (isArray(quickmessages)) {
      quickmessages.forEach((quickemessage) => {
        const quickemessageIndex = state.findIndex((u) => u.id === quickemessage.id);
        if (quickemessageIndex !== -1) {
          state[quickemessageIndex] = quickemessage;
        } else {
          newQuickmessages.push(quickemessage);
        }
      });
    }

    return [...state, ...newQuickmessages];
  }

  if (action.type === "UPDATE_QUICKMESSAGES") {
    const quickemessage = action.payload;
    const quickemessageIndex = state.findIndex((u) => u.id === quickemessage.id);

    if (quickemessageIndex !== -1) {
      state[quickemessageIndex] = quickemessage;
      return [...state];
    } else {
      return [quickemessage, ...state];
    }
  }

  if (action.type === "DELETE_QUICKMESSAGE") {
    const quickemessageId = action.payload;

    const quickemessageIndex = state.findIndex((u) => u.id === quickemessageId);
    if (quickemessageIndex !== -1) {
      state.splice(quickemessageIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

export const MEDIA_ICONS = {
  image: ImageOutlinedIcon,
  audio: MicNoneIcon,
  video: VideocamOutlinedIcon,
  pdf: PictureAsPdfOutlinedIcon,
  document: InsertDriveFileOutlinedIcon,
};

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  const borderInput = isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)";
  const purpleText = stateText(theme, "#C08BFF", "purple");
  const cyanText = stateText(theme, AEROS.cyan, "info");
  return {
    pageHeader: {
      minHeight: 60,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "0 12px",
      borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.06)"}`,
    },
    pageTitle: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 16,
      fontWeight: 700,
      letterSpacing: "-0.02em",
      color: theme.palette.text.primary,
      lineHeight: 1.3,
    },
    pageTitleCount: { color: theme.palette.text.secondary, fontWeight: 500 },
    pageSubtitle: { fontSize: 12, color: theme.palette.text.secondary, lineHeight: 1.3 },
    mono: {
      color: cyanText,
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    },
    headerActions: {
      marginLeft: "auto",
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexWrap: "wrap",
    },
    searchPill: {
      width: 230,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderInput}`,
      borderRadius: 10,
      padding: "5px 14px",
      fontSize: 13,
      color: theme.palette.text.primary,
      display: "flex",
      alignItems: "center",
      gap: 8,
      "& .MuiSvgIcon-root": { fontSize: 18, color: theme.palette.text.secondary },
      "& input::placeholder": { color: theme.palette.text.secondary, opacity: 1 },
    },
    gradientButton: {
      borderRadius: 10,
      padding: "8px 16px",
      fontSize: 13,
      fontWeight: 700,
      "& .MuiButton-startIcon": { marginRight: 4 },
      "& .MuiSvgIcon-root": { fontSize: 17 },
    },
    pageBody: {
      flex: 1,
      padding: "16px 12px",
      overflowY: "auto",
      ...theme.scrollbarStyles,
    },
    // ---- filtros ----
    filterBar: {
      display: "flex",
      gap: 8,
      marginBottom: 18,
      alignItems: "center",
      flexWrap: "wrap",
    },
    filterPill: {
      borderRadius: 99,
      padding: "5px 14px",
      font: "inherit",
      fontSize: 12,
      fontWeight: 600,
      lineHeight: 1.5,
      color: theme.palette.text.secondary,
      background: "transparent",
      border: `1px solid ${borderInput}`,
      display: "flex",
      gap: 6,
      alignItems: "center",
      cursor: "pointer",
      "&:hover": { color: theme.palette.text.primary },
    },
    filterPillActive: {
      color: purpleText,
      backgroundColor: "rgba(160,68,255,0.15)",
      borderColor: "transparent",
      "&:hover": { color: purpleText },
    },
    filterCount: { fontSize: 10.5, opacity: 0.7, fontVariantNumeric: "tabular-nums" },
    viewToggle: {
      marginLeft: "auto",
      display: "flex",
      gap: 4,
      border: `1px solid ${borderInput}`,
      borderRadius: 9,
      padding: 3,
    },
    viewButton: {
      width: 28,
      height: 26,
      borderRadius: 6,
      padding: 0,
      color: theme.palette.text.secondary,
      "& .MuiSvgIcon-root": { fontSize: 16 },
      "&:hover": { backgroundColor: "transparent", color: theme.palette.text.primary },
    },
    viewButtonActive: {
      backgroundColor: "rgba(160,68,255,0.2)",
      color: purpleText,
      "&:hover": { backgroundColor: "rgba(160,68,255,0.2)", color: purpleText },
    },
    // ---- grid de cards ----
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: 14,
      [theme.breakpoints.down("md")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    list: { display: "flex", flexDirection: "column", gap: 8 },
    card: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 14,
      padding: 16,
      display: "flex",
      flexDirection: "column",
      gap: 10,
      minWidth: 0,
      transition: "border-color 0.2s ease",
      "&:hover": { borderColor: "rgba(160,68,255,0.4)" },
    },
    cardRow: {
      flexDirection: "row",
      alignItems: "center",
      padding: "10px 16px",
      borderRadius: 12,
      gap: 12,
    },
    cardHead: { display: "flex", alignItems: "center", gap: 8, minWidth: 0 },
    shortcode: {
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      fontSize: 12.5,
      fontWeight: 700,
      color: cyanText,
      backgroundColor: alpha(AEROS.cyan, 0.1),
      borderRadius: 7,
      padding: "3px 9px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "100%",
    },
    mediaChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      fontSize: 10.5,
      fontWeight: 600,
      color: purpleText,
      backgroundColor: "rgba(160,68,255,0.12)",
      borderRadius: 6,
      padding: "2px 7px",
      whiteSpace: "nowrap",
      "& .MuiSvgIcon-root": { fontSize: 12 },
    },
    visPill: {
      marginLeft: "auto",
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      fontSize: 10.5,
      fontWeight: 600,
      borderRadius: 99,
      padding: "2px 9px",
      whiteSpace: "nowrap",
      "& .MuiSvgIcon-root": { fontSize: 12 },
    },
    visTeam: {
      color: stateText(theme, AEROS.success, "success"),
      backgroundColor: alpha(AEROS.success, 0.1),
    },
    visPersonal: {
      color: theme.palette.text.secondary,
      backgroundColor: isLight ? "rgba(15,20,40,0.06)" : "rgba(255,255,255,0.06)",
    },
    message: {
      fontSize: 13,
      lineHeight: 1.55,
      color: theme.palette.text.primary,
      display: "-webkit-box",
      WebkitLineClamp: 3,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
      minHeight: 60,
      whiteSpace: "pre-line",
      wordBreak: "break-word",
    },
    messageRow: {
      WebkitLineClamp: 1,
      minHeight: 0,
      flex: 1,
      fontSize: 12.5,
      color: theme.palette.text.secondary,
    },
    cardFoot: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      paddingTop: 10,
      borderTop: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.06)"}`,
    },
    cardFootRow: { paddingTop: 0, borderTop: "none", marginLeft: "auto" },
    owner: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: 11,
      color: theme.palette.text.secondary,
      minWidth: 0,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    avatar: {
      width: 20,
      height: 20,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 8,
      fontWeight: 800,
      color: "#07080D",
      flexShrink: 0,
    },
    uses: {
      fontSize: 11,
      color: theme.palette.aeros.textTertiary,
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
    },
    actions: { marginLeft: "auto", display: "flex", gap: 5 },
    iconButton: {
      width: 28,
      height: 28,
      borderRadius: 8,
      border: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.secondary,
      padding: 0,
      "& .MuiSvgIcon-root": { fontSize: 15 },
      "&:hover": {
        color: AEROS.cyan,
        borderColor: alpha(AEROS.cyan, 0.4),
        backgroundColor: "transparent",
      },
    },
    iconButtonDanger: {
      "&:hover": { color: AEROS.error, borderColor: alpha(AEROS.error, 0.4) },
    },
    empty: {
      gridColumn: "1 / -1",
      border: `1px dashed ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.12)"}`,
      borderRadius: 14,
      padding: "40px 16px",
      textAlign: "center",
      color: theme.palette.text.secondary,
      fontSize: 13,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 10,
    },
    emptyIcon: {
      width: 40,
      height: 40,
      borderRadius: 12,
      backgroundColor: "rgba(160,68,255,0.12)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "& .MuiSvgIcon-root": { fontSize: 20, color: purpleText },
    },
    skeleton: { borderRadius: 14, height: 168 },
  };
});

const QuickMessages = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedQuickemessage, setSelectedQuickemessage] = useState(null);
  const [deletingQuickemessage, setDeletingQuickemessage] = useState(null);
  const [quickemessageModalOpen, setQuickMessageDialogOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem("quickMessagesView") || "grid";
    } catch (e) {
      return "grid";
    }
  });
  const [quickemessages, dispatch] = useReducer(reducer, []);
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  const [counts, setCounts] = useState({ all: 0, team: 0, mine: 0, media: 0 });

  const fetchQuickemessages = useCallback(async () => {
    try {
      const { data } = await api.get("/quick-messages", {
        params: { searchParam, pageNumber, filter },
      });

      dispatch({ type: "LOAD_QUICKMESSAGES", payload: data.records });
      setHasMore(data.hasMore);
      if (data.counts) setCounts(data.counts);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  }, [searchParam, pageNumber, filter]);

  // Filtro é aplicado no servidor: troca zera a paginação
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [filter]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchQuickemessages();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, fetchQuickemessages]);

  useEffect(() => {
    const companyId = user.companyId;

    const onQuickMessageEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUICKMESSAGES", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUICKMESSAGE", payload: +data.id });
      }
    };
    socket.on(`company-${companyId}-quickmessage`, onQuickMessageEvent);

    return () => {
      socket.off(`company-${companyId}-quickmessage`, onQuickMessageEvent);
    };
  }, [socket, user.companyId]);

  const handleOpenQuickMessageDialog = () => {
    setSelectedQuickemessage(null);
    setQuickMessageDialogOpen(true);
  };

  const handleCloseQuickMessageDialog = () => {
    setSelectedQuickemessage(null);
    setQuickMessageDialogOpen(false);
    fetchQuickemessages();
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditQuickemessage = (quickemessage) => {
    setSelectedQuickemessage(quickemessage);
    setQuickMessageDialogOpen(true);
  };

  const handleDeleteQuickemessage = async (quickemessageId) => {
    try {
      await api.delete(`/quick-messages/${quickemessageId}`);
      toast.success(i18n.t("quickMessages.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingQuickemessage(null);
    setSearchParam("");
    setPageNumber(1);
    dispatch({ type: "RESET" });
    fetchQuickemessages();
  };

  const handleCopy = async (quickemessage) => {
    try {
      await navigator.clipboard.writeText(quickemessage.message || "");
      toast.success(i18n.t("quickMessages.toasts.copied"));
    } catch (err) {
      toastError(err);
    }
  };

  const changeView = (next) => {
    setView(next);
    try {
      localStorage.setItem("quickMessagesView", next);
    } catch (e) {
      // sem storage: só não persiste a escolha
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  // O servidor já filtra e busca; o filtro local só refina o que o socket
  // acrescenta (create/update chegam sem passar pelo filtro ativo).
  const visible = useMemo(
    () => applyFilters(quickemessages, { filter, search: searchParam, currentUserId: user.id }),
    [quickemessages, filter, searchParam, user.id]
  );

  const ownerOf = (qm) => {
    if (qm.user?.name) return qm.user.name;
    if (Number(qm.userId) === Number(user.id)) return user.name;
    return null;
  };

  const avatarColor = (name) => {
    const cycle = AEROS.avatarCycle;
    let h = 0;
    for (const ch of String(name || "")) h = (h + ch.charCodeAt(0)) % cycle.length;
    return cycle[h];
  };

  const isList = view === "list";

  const renderCard = (qm) => {
    const kind = mediaKind(qm);
    const MediaIcon = kind ? MEDIA_ICONS[kind] : null;
    const shared = isShared(qm);
    const owner = ownerOf(qm);
    return (
      <div key={qm.id} className={`${classes.card} ${isList ? classes.cardRow : ""}`}>
        <div className={classes.cardHead} style={isList ? { flex: "0 0 auto", maxWidth: "45%" } : undefined}>
          <span className={classes.shortcode}>/{qm.shortcode}</span>
          {kind && (
            <span className={classes.mediaChip}>
              <MediaIcon />
              {i18n.t(`quickMessages.media.${kind}`)}
            </span>
          )}
          {!isList && (
            <span className={`${classes.visPill} ${shared ? classes.visTeam : classes.visPersonal}`}>
              {shared ? <GroupOutlinedIcon /> : <LockOutlinedIcon />}
              {i18n.t(shared ? "quickMessages.visibility.team" : "quickMessages.visibility.personal")}
            </span>
          )}
        </div>
        <div className={`${classes.message} ${isList ? classes.messageRow : ""}`} title={qm.message}>
          {qm.message}
        </div>
        <div className={`${classes.cardFoot} ${isList ? classes.cardFootRow : ""}`}>
          {isList && (
            <span className={`${classes.visPill} ${shared ? classes.visTeam : classes.visPersonal}`} style={{ marginLeft: 0 }}>
              {shared ? <GroupOutlinedIcon /> : <LockOutlinedIcon />}
              {i18n.t(shared ? "quickMessages.visibility.team" : "quickMessages.visibility.personal")}
            </span>
          )}
          {owner && (
            <div className={classes.owner}>
              <div className={classes.avatar} style={{ backgroundColor: avatarColor(owner) }}>
                {initials(owner)}
              </div>
              {owner}
            </div>
          )}
          <div className={classes.uses}>
            · {i18n.t("quickMessages.uses", { count: Number(qm.usageCount) || 0 })}
          </div>
          <div className={classes.actions}>
            <Tooltip title={i18n.t("quickMessages.actions.copy")}>
              <IconButton className={classes.iconButton} onClick={() => handleCopy(qm)}>
                <FileCopyOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={i18n.t("quickMessages.actions.edit")}>
              <IconButton className={classes.iconButton} onClick={() => handleEditQuickemessage(qm)}>
                <EditOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={i18n.t("quickMessages.actions.delete")}>
              <IconButton
                className={`${classes.iconButton} ${classes.iconButtonDanger}`}
                onClick={() => {
                  setConfirmModalOpen(true);
                  setDeletingQuickemessage(qm);
                }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </div>
    );
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingQuickemessage &&
          `${i18n.t("quickMessages.confirmationModal.deleteTitle")} /${deletingQuickemessage.shortcode}?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteQuickemessage(deletingQuickemessage.id)}
      >
        {i18n.t("quickMessages.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <QuickMessageDialog
        resetPagination={() => {
          setPageNumber(1);
          fetchQuickemessages();
        }}
        open={quickemessageModalOpen}
        onClose={handleCloseQuickMessageDialog}
        quickemessageId={selectedQuickemessage && selectedQuickemessage.id}
        existing={quickemessages}
      />

      <div className={classes.pageHeader}>
        <div>
          <Typography component="div" className={classes.pageTitle}>
            {i18n.t("quickMessages.title")}{" "}
            <span className={classes.pageTitleCount}>· {counts.all}</span>
          </Typography>
          <div className={classes.pageSubtitle}>
            {i18n.t("quickMessages.subtitleBefore")}{" "}
            <span className={classes.mono}>/atalho</span>{" "}
            {i18n.t("quickMessages.subtitleAfter")}
          </div>
        </div>
        <div className={classes.headerActions}>
          <InputBase
            className={classes.searchPill}
            placeholder={i18n.t("quickMessages.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            startAdornment={<SearchIcon />}
            inputProps={{ "aria-label": i18n.t("quickMessages.searchPlaceholder") }}
          />
          <Button
            variant="contained"
            color="primary"
            className={classes.gradientButton}
            startIcon={<AddIcon />}
            onClick={handleOpenQuickMessageDialog}
          >
            {i18n.t("quickMessages.buttons.add")}
          </Button>
        </div>
      </div>

      <div className={classes.pageBody} onScroll={handleScroll}>
        <div className={classes.filterBar}>
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              className={`${classes.filterPill} ${filter === f ? classes.filterPillActive : ""}`}
              onClick={() => setFilter(f)}
              aria-pressed={filter === f}
            >
              {i18n.t(`quickMessages.filters.${f}`)}
              <span className={classes.filterCount}>{counts[f]}</span>
            </button>
          ))}
          <div className={classes.viewToggle}>
            <IconButton
              className={`${classes.viewButton} ${!isList ? classes.viewButtonActive : ""}`}
              onClick={() => changeView("grid")}
              aria-label={i18n.t("quickMessages.view.grid")}
            >
              <GridViewIcon />
            </IconButton>
            <IconButton
              className={`${classes.viewButton} ${isList ? classes.viewButtonActive : ""}`}
              onClick={() => changeView("list")}
              aria-label={i18n.t("quickMessages.view.list")}
            >
              <ViewListIcon />
            </IconButton>
          </div>
        </div>

        <div className={isList ? classes.list : classes.grid}>
          {visible.map(renderCard)}
          {loading &&
            [0, 1, 2].map((i) => (
              <Skeleton key={`sk-${i}`} variant="rect" className={classes.skeleton} />
            ))}
          {!loading && visible.length === 0 && (
            <div className={classes.empty}>
              <div className={classes.emptyIcon}>
                <FlashOnIcon />
              </div>
              {i18n.t(searchParam || filter !== "all" ? "quickMessages.noResultsFound" : "quickMessages.noQuickMessages")}
            </div>
          )}
        </div>
      </div>
    </MainContainer>
  );
};

export default QuickMessages;
